// Approval & signing: SWI recommends (signs in PM C3), the architect certifies (in PM C3, or SWI records a hand-signed
// copy), the certificate pack is one PDF, client finance gets it by email, and the claim is marked paid / not paid.
import { useState } from 'react';
import { api, apiBlob } from '../../lib/api';
import { useApp } from '../../lib/app';
import { money } from '../../lib/claimCalc';
import { fmtDate, fmtDateTime } from '../../lib/format';
import type { ClaimDetails, ClaimModel, Signature } from '../../lib/claimData';
import type { FileRow } from '../ItemFiles';
import { Button, errText, useToast } from '../kit';

export function Stamp({ sig, swi }: { sig: Signature | undefined; swi: boolean }) {
  if (!sig)
    return (
      <div className="stamp empty">
        <span className="muted small">{swi ? 'Awaiting SWI signature' : 'Awaiting architect certification'}</span>
      </div>
    );
  return (
    <div className={`stamp ${swi ? 'swi' : 'arch'}`}>
      <svg width="50" height="50" viewBox="0 0 58 58" role="img" aria-label={`${swi ? 'SWI approval' : 'Architect certified'} stamp`}>
        <circle cx="29" cy="29" r="26" fill="none" stroke="currentColor" strokeWidth="2.5" />
        <circle cx="29" cy="29" r="20" fill="none" stroke="currentColor" strokeWidth="1" />
        <text x="29" y="27" textAnchor="middle" fontFamily="Arial" fontSize={swi ? 10 : 6.2} fontWeight="700" fill="currentColor">
          {swi ? 'SWI' : (sig.org_name ?? 'ARCHITECT').toUpperCase().slice(0, 11)}
        </text>
        <text x="29" y="37" textAnchor="middle" fontFamily="Arial" fontSize="6" fontWeight="700" fill="currentColor">
          {swi ? 'APPROVED' : 'CERTIFIED'}
        </text>
      </svg>
      <div className="stamp-text">
        <strong>{sig.full_name}</strong>
        <span>
          {sig.role_title} · {sig.org_name}
        </span>
        <span className="muted">
          {sig.method === 'upload' ? 'Signed by hand, copy uploaded' : 'Signed in PM C3'} · {fmtDateTime(sig.signed_at)}
        </span>
      </div>
    </div>
  );
}

export function Approval({
  itemId,
  details,
  model,
  signatures,
  files,
  architectOrgId,
  onChanged,
}: {
  itemId: string;
  details: ClaimDetails;
  model: ClaimModel;
  signatures: Signature[];
  files: FileRow[];
  architectOrgId: string | null;
  onChanged: () => void;
}) {
  const { access, can, profile } = useApp();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [hand, setHand] = useState<{ name: string; file: string } | null>(null);
  const [paid, setPaid] = useState<{ on: string; amount: string } | null>(null);
  const swiSig = signatures.find((s) => s.kind === 'swi');
  const archSig = signatures.find((s) => s.kind === 'architect');
  const isSuper = Boolean(access?.super_admin);
  const internal = isSuper || Boolean(access?.roles.some((r) => ['pm', 'swi_finance', 'field'].includes(r.role)));
  const canSwi = internal && can('claims', 4);
  const isArchitect = Boolean(access?.roles.some((r) => r.role === 'architect')) && Boolean(architectOrgId) && profile?.org_id === architectOrgId;
  const certified = Boolean(details.locked_at);

  async function run(label: string, fn: () => Promise<unknown>, ok: string) {
    setBusy(label);
    try {
      await fn();
      toast({ tone: 'ok', text: ok });
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
    }
  }
  const signSwi = () => {
    if (!window.confirm(`Sign for SWI and recommend FJD ${money(model.swi.payableVip)} VIP for payment? The figures are saved with your signature.`)) return;
    void run('sign', () => api('cert-sign', { item_id: itemId, kind: 'swi', assessment: { swi: model.swi, claimed: model.claimed, before: model.before, approvedVariations: model.approvedVariations } }), 'Signed for SWI');
  };
  const signArch = () => {
    if (!window.confirm(`Certify FJD ${money(model.swi.payableVip)} VIP as architect of record? Your full name, role, date and time go on the certificate.`)) return;
    void run('sign', () => api('cert-sign', { item_id: itemId, kind: 'architect', method: 'pm_c3' }), 'Certified — the claim is now locked');
  };
  const revoke = (s: Signature) => {
    const reason = window.prompt(`Take back ${s.full_name}'s signature? This unlocks the claim. Reason:`);
    if (!reason) return;
    void run('revoke', () => api('cert-sign', { action: 'revoke', signature_id: s.id, reason }), 'Signature taken back');
  };
  async function preview() {
    setBusy('pdf');
    try {
      const { url } = await apiBlob(`cert-pdf?item_id=${encodeURIComponent(itemId)}`);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
    }
  }
  const sendFinance = () => {
    const note = window.prompt('Email the certificate pack to client finance. Add a note (optional):', certified ? '' : 'Draft for your information — not yet certified.');
    if (note === null) return;
    void run('send', () => api('claim-action', { action: 'send_finance', item_id: itemId, note }), 'Sent to client finance');
  };
  const pdfs = files.filter((f) => f.is_current && f.kind === 'file' && /pdf|image/i.test(f.mime ?? ''));
  const paidOn = details.paid_on;

  const steps: { n: number; done: boolean; title: string; sub: React.ReactNode }[] = [
    { n: 1, done: true, title: 'Submitted by PBS', sub: details.period_to ? `Period to ${fmtDate(details.period_to)}` : 'Claim received' },
    { n: 2, done: Boolean(swiSig), title: 'SWI review & recommendation', sub: swiSig ? `Signed by ${swiSig.full_name}` : 'SWI checks the lines and variations, then signs' },
    { n: 3, done: Boolean(archSig), title: 'Architect certifies', sub: archSig ? `Certified by ${archSig.full_name}${archSig.method === 'upload' ? ' (hand-signed copy)' : ''}` : 'Signs in PM C3 — or signs by hand and SWI uploads it' },
    { n: 4, done: Boolean(paidOn), title: 'Client finance approval', sub: 'Certificate pack emailed as one PDF' },
    { n: 5, done: Boolean(paidOn), title: paidOn ? 'Paid' : 'Not paid', sub: paidOn ? `${fmtDate(paidOn)} · FJD ${money(details.paid_amount)}` : 'Mark paid when client finance confirms — date and amount' },
  ];

  return (
    <div className="approval">
      <ol className="steps">
        {steps.map((s) => (
          <li key={s.n} className={s.done ? 'done' : ''}>
            <span className="sd" aria-hidden="true">
              {s.done ? '✓' : s.n}
            </span>
            <div>
              <strong>{s.title}</strong>
              <div className="muted small">{s.sub}</div>
            </div>
          </li>
        ))}
      </ol>
      <div className="stamps">
        <div>
          <div className="k">Architect of record</div>
          <Stamp sig={archSig} swi={false} />
          {archSig && isSuper ? (
            <button type="button" className="linkbtn danger small" onClick={() => revoke(archSig)}>
              Take back
            </button>
          ) : null}
        </div>
        <div>
          <div className="k">Owner’s representative (SWI)</div>
          <Stamp sig={swiSig} swi />
          {swiSig && isSuper && !archSig ? (
            <button type="button" className="linkbtn danger small" onClick={() => revoke(swiSig)}>
              Take back
            </button>
          ) : null}
        </div>
      </div>
      <div className="resp-row">
        {!swiSig && canSwi ? (
          <Button kind="primary" onClick={signSwi} disabled={Boolean(busy)}>
            Sign as SWI · {profile?.full_name ?? ''}
          </Button>
        ) : null}
        {swiSig && !archSig && isArchitect ? (
          <Button kind="primary" onClick={signArch} disabled={Boolean(busy)}>
            Certify as architect
          </Button>
        ) : null}
        {swiSig && !archSig && canSwi ? (
          <Button onClick={() => setHand(hand ? null : { name: '', file: pdfs[0]?.id ?? '' })} disabled={Boolean(busy)}>
            Record hand-signed copy
          </Button>
        ) : null}
        <Button onClick={() => void preview()} disabled={Boolean(busy)}>
          {busy === 'pdf' ? 'Making the PDF…' : certified ? 'Certificate pack (PDF)' : 'Preview certificate pack'}
        </Button>
        {canSwi ? (
          <Button onClick={sendFinance} disabled={Boolean(busy)}>
            Email client finance
          </Button>
        ) : null}
        {canSwi && certified ? (
          paidOn ? (
            <Button kind="ghost" onClick={() => void run('paid', () => api('claim-action', { action: 'paid', item_id: itemId, paid_on: null }), 'Marked not paid')}>
              Mark not paid
            </Button>
          ) : (
            <Button kind="ghost" onClick={() => setPaid({ on: new Date().toISOString().slice(0, 10), amount: String(model.swi.payableVip) })}>
              Mark paid
            </Button>
          )
        ) : null}
      </div>
      {hand ? (
        <div className="fu-form">
          <input value={hand.name} onChange={(e) => setHand({ ...hand, name: e.target.value })} placeholder="Architect's full name as signed" aria-label="Architect's full name" />
          <select value={hand.file} onChange={(e) => setHand({ ...hand, file: e.target.value })} aria-label="Signed copy">
            <option value="">Signed copy (upload it to Files first)…</option>
            {pdfs.map((f) => (
              <option key={f.id} value={f.id}>
                {f.original_name}
              </option>
            ))}
          </select>
          <Button
            kind="primary"
            disabled={!hand.name.trim() || !hand.file || Boolean(busy)}
            onClick={() => void run('sign', () => api('cert-sign', { item_id: itemId, kind: 'architect', method: 'upload', full_name: hand.name.trim(), file_id: hand.file }), 'Recorded — the claim is now certified and locked').then(() => setHand(null))}
          >
            Save
          </Button>
          <Button onClick={() => setHand(null)}>Cancel</Button>
        </div>
      ) : null}
      {paid ? (
        <div className="fu-form">
          <input type="date" value={paid.on} onChange={(e) => setPaid({ ...paid, on: e.target.value })} aria-label="Paid on" />
          <input inputMode="decimal" value={paid.amount} onChange={(e) => setPaid({ ...paid, amount: e.target.value })} aria-label="Amount paid (VIP)" />
          <Button kind="primary" disabled={!paid.on || Boolean(busy)} onClick={() => void run('paid', () => api('claim-action', { action: 'paid', item_id: itemId, paid_on: paid.on, amount: Number(paid.amount) }), 'Marked paid').then(() => setPaid(null))}>
            Save
          </Button>
          <Button onClick={() => setPaid(null)}>Cancel</Button>
        </div>
      ) : null}
      {certified ? <p className="small muted">Certified {fmtDateTime(details.certified_at)} · ref {details.cert_ref} · locked — changes after this are amendments by the Super Admin, with a reason.</p> : null}
    </div>
  );
}
